import type { ProductOptionGroupFragment, SearchOptionFragment, SearchOptionGroupFragment, SearchProductQuery, SearchProductVariantResultFragment, SearchResultAssetFragment } from '#graphql-operations'
import { COLOR_PRODUCT_OPTION_GROUP, SIZE_PRODUCT_OPTION_GROUP } from '~/constants'

export const useProductStore = defineStore('product', () => {
  const route = useRoute()
  const router = useRouter()

  const { ecommerce } = useAnalytics()
  const selectedOptions = ref<Record<string, string>>({})
  const hoveredOption = ref<Record<string, string>>({})
  const status = ref<'pending' | 'success' | 'error'>('pending')
  const product = ref<SearchProductQuery['searchProduct'] | null>(null)
  const queryInitialized = ref(false)

  const fetchProduct = async (productId: string) => {
    if (!product.value || product.value.id !== productId) {
      try {
        const { data } = await useGraphqlQuery('searchProduct', { id: productId }) as { data: SearchProductQuery }
        product.value = data.searchProduct
        status.value = 'success'
      }
      catch {
        status.value = 'error'
      }
    }

    return product.value
  }

  const reset = () => {
    product.value = null
    status.value = 'pending'
  }

  const handleProductOptionMouseOver = (optionGroupCode: string, optionCode: string) => {
    hoveredOption.value = { [optionGroupCode]: optionCode }
  }

  const handleProductOptionMouseLeave = () => {
    hoveredOption.value = {}
  }

  const productCollections = computed(() => product.value?.collections ?? [])

  const productCollectionId = computed(() => {
    return productCollections.value?.[productCollections.value?.length - 1]?.id
  })

  const productBreadcrumbs = computed(() => {
    if (!productCollections.value.length)
      return []

    return productCollections.value.map(crumb => ({
      ...crumb,
      path: `/collections/${crumb.id}/${crumb.slug}`,
    }))
  })

  const productVariants = computed(() => product.value?.variants)

  const productOptionGroups = computed(() => {
    return product.value?.optionGroups.reduce((acc, optionGroup) => ({
      ...acc,
      [optionGroup.code]: optionGroup,
    }), {} as Record<string, SearchOptionGroupFragment & {
      options?: SearchOptionFragment[] | null
    }>)
  })

  const variantLookupMap = computed(() => {
    const map = new Map<string, SearchProductVariantResultFragment>()

    productVariants.value?.forEach((variant) => {
      if (variant.options) {
        const key = variant.options
          .map(opt => `${opt.group.code}:${opt.code}`)
          .sort()
          .join('|')
        map.set(key, variant)
      }
    })

    return map
  })

  const selectedVariant = computed(() => {
    if (!Object.keys(selectedOptions.value).length)
      return undefined

    const key = Object.entries(selectedOptions.value)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([group, code]) => `${group}:${code}`)
      .join('|')

    return variantLookupMap.value.get(key)
  })

  const debouncedUpdateQueryParams = useDebounceFn(updateQueryParams, 150)

  const defaultVariant = computed(() => productVariants.value?.length ? productVariants.value[0] : undefined)

  const activeVariant = computed(() => selectedVariant.value ?? defaultVariant.value)

  const priceBeforeDiscount = computed(() => activeVariant.value?.customProductVariantMappings.priceBeforeDiscount)

  const price = computed(() => activeVariant.value?.price)

  const amountSaved = computed(() => {
    if (!priceBeforeDiscount.value || !price.value || priceBeforeDiscount.value <= price.value)
      return 0
    return priceBeforeDiscount.value - price.value
  })

  const discountPercentage = computed(() => {
    if (!priceBeforeDiscount.value || !price.value || priceBeforeDiscount.value <= price.value)
      return 0
    return Math.round(((priceBeforeDiscount.value - price.value) / priceBeforeDiscount.value) * 100)
  })

  const availableProductOptions = computed(() => {
    const optionGroupMap = new Map<string, Map<SearchOptionGroupFragment['code'], SearchOptionFragment & { group: SearchOptionGroupFragment, featuredAsset?: SearchProductVariantResultFragment['featuredAsset'] }>>()

    productVariants.value?.forEach((variant) => {
      variant.options?.forEach((option) => {
        const optionGroupCode = option.group.code

        if (!optionGroupMap.has(optionGroupCode))
          optionGroupMap.set(optionGroupCode, new Map())

        const optionsMap = optionGroupMap.get(optionGroupCode)!

        if (!optionsMap.has(option.code)) {
          optionsMap.set(option.code, COLOR_PRODUCT_OPTION_GROUP.includes(optionGroupCode)
            ? { ...option, featuredAsset: variant.featuredAsset }
            : { ...option }) // deconstruct to make unreactive
        }
      })
    })

    const result: Record<ProductOptionGroupFragment['code'], Record<SearchOptionFragment['code'], SearchOptionFragment & { group: SearchOptionGroupFragment, featuredAsset?: SearchProductVariantResultFragment['featuredAsset'] }>> = {}

    // Create an array of option group codes, sorted with COLOR_PRODUCT_OPTION_GROUP first
    const sortedOptionGroupCodes = Array.from(optionGroupMap.keys()).sort((a, b) => {
      if (COLOR_PRODUCT_OPTION_GROUP.includes(a))
        return -1
      if (COLOR_PRODUCT_OPTION_GROUP.includes(b))
        return 1
      return 0
    })

    // Add the sorted option groups to the result object
    sortedOptionGroupCodes.forEach((optionGroupCode) => {
      const optionsMap = optionGroupMap.get(optionGroupCode)!
      const optionsObject: Record<string, SearchOptionFragment & { group: SearchOptionGroupFragment, featuredAsset?: SearchProductVariantResultFragment['featuredAsset'] }> = {}

      optionsMap.forEach((value, key) => optionsObject[key] = value)

      result[optionGroupCode] = optionGroupCode === SIZE_PRODUCT_OPTION_GROUP
        ? sortGradeList(Object.keys(optionsObject) as string[]).reduce((sortedObj, code) => {
            if (optionsObject[code])
              sortedObj[code] = optionsObject[code]
            return sortedObj
          }, {} as Record<string, SearchOptionFragment & { group: SearchOptionGroupFragment, featuredAsset?: SearchProductVariantResultFragment['featuredAsset'] }>)
        : optionsObject
    })

    return result
  })

  const inStockVariantCombos = computed(() => {
    const combos = new Set<string>()

    productVariants.value?.forEach((variant) => {
      if (variant.stockLevel === 'IN_STOCK' && variant.options) {
        const combo = variant.options
          .slice()
          .sort((a, b) => a.group.code.localeCompare(b.group.code))
          .map(opt => `${opt.group.code}:${opt.code}`)
          .join('|')
        combos.add(combo)
      }
    })

    return combos
  })

  const availableOptionsMap = computed(() => {
    const map: Record<string, Record<string, boolean>> = {}

    // Early return if no data
    if (!availableProductOptions.value || !productVariants.value)
      return map

    const selectedCount = Object.keys(selectedOptions.value).length
    const optionGroupCount = Object.keys(availableProductOptions.value).length

    for (const [groupCode, options] of Object.entries(availableProductOptions.value)) {
      map[groupCode] = {}

      for (const optionCode in options) {
        // Fast paths first
        if (selectedCount === 0
          || (optionGroupCount > 1 && selectedCount === 1)
          || selectedOptions.value[groupCode] === optionCode) {
          map[groupCode][optionCode] = true
          continue
        }

        // Check using pre-computed set
        const testOptions = { ...selectedOptions.value, [groupCode]: optionCode }
        const testKey = Object.entries(testOptions)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([g, c]) => `${g}:${c}`)
          .join('|')

        map[groupCode][optionCode] = inStockVariantCombos.value.has(testKey)
      }
    }

    return map
  })

  const previewOptionsMap = computed(() => {
    const map: Record<string, Record<string, boolean>> = {}

    if (!availableProductOptions.value || !productVariants.value)
      return map

    for (const [groupCode, options] of Object.entries(availableProductOptions.value)) {
      map[groupCode] = {}
      for (const optionCode in options) {
        // If no options have been selected yet, every option is available
        if (Object.keys(hoveredOption.value).length === 0) {
          map[groupCode][optionCode] = true
          continue
        }

        // Create a test object that includes the current option for this group.
        const testSelectedOptions = { ...hoveredOption.value, [groupCode]: optionCode }

        // When there is more than one option group, and only one option is selected,
        // all options in that group should be available.
        if (Object.keys(availableProductOptions.value).length > 1 && Object.keys(testSelectedOptions).length === 1) {
          map[groupCode][optionCode] = true
          continue
        }

        // If the current option is already selected, it should always be available (to allow deselection).
        if (hoveredOption.value[groupCode] && hoveredOption.value[groupCode] === optionCode) {
          map[groupCode][optionCode] = true
          continue
        }

        // Otherwise, check if any variant has a valid combination with this option
        map[groupCode][optionCode] = productVariants.value.some((variant) => {
          const matchingVariant = variant.options?.every((option) => {
            return testSelectedOptions[option.group.code] === option.code
          })
          return matchingVariant && variant.stockLevel === 'IN_STOCK'
        })
      }
    }

    return map
  })

  const allOptionsSelected = computed(() => {
    const requiredOptionGroups = Object.keys(productOptionGroups.value || {})
    return requiredOptionGroups.every(groupCode => !!selectedOptions.value[groupCode])
  })

  function handleOptionChange(optionGroupCode: string, optionCode: string) {
    if (selectedOptions.value[optionGroupCode] === optionCode)
      return

    const hasValidCombination = productVariants.value?.some((variant) => {
      const matchesAllOptions = variant.options?.every((option) => {
        if (option.group.code === optionGroupCode)
          return option.code === optionCode
        return selectedOptions.value[option.group.code] === option.code
      })

      return matchesAllOptions && variant.stockLevel === 'IN_STOCK'
    })

    if (!hasValidCombination) {
      selectedOptions.value = {
        [optionGroupCode]: optionCode,
      }
    }
    else {
      // Update the selection for this option group
      selectedOptions.value[optionGroupCode] = optionCode
    }

    debouncedUpdateQueryParams()
  }

  function isColorGroupWithImages(optionGroupCode: string) {
    if (optionGroupCode !== COLOR_PRODUCT_OPTION_GROUP)
      return false

    return Object.values(availableProductOptions.value[optionGroupCode] ?? {}).every(option => option.featuredAsset?.preview)
  }

  function initializeFromQuery() {
    if (!productOptionGroups.value)
      return

    const queryOptions = { ...route.query }
    const variantId = queryOptions.variantId as string

    if (variantId) {
      // Find the variant with matching ID
      const variant = productVariants.value?.find(v => v.id === variantId)

      if (variant) {
        // Set options based on the found variant
        variant.options?.forEach((option) => {
          selectedOptions.value[option.group.code] = option.code
        })
        queryInitialized.value = true
        return
      }
    }

    const validOptionGroups = Object.keys(productOptionGroups.value)

    // Filter out any query parameters that don't correspond to valid option groups
    const validQueryOptions = Object.entries(queryOptions)
      .filter(([key]) => validOptionGroups.includes(key))
      .reduce((acc, [key, value]) => ({
        ...acc,
        [key]: typeof value === 'string' ? value : '',
      }), {} as Record<string, string>)

    const wouldBeOutOfStock = productVariants.value?.some((variant) => {
      const matchesAllOptions = variant.options?.every((option) => {
        const queryOption = validQueryOptions[option.group.code]
        return queryOption === option.code
      })

      return matchesAllOptions && variant.stockLevel !== 'IN_STOCK'
    })

    // Only set options that correspond to available variants
    if (wouldBeOutOfStock) {
      // If the combination would be out of stock, only set the first valid in-stock option
      const [firstGroup, firstOption] = Object.entries(validQueryOptions)[0] || []
      if (firstGroup && firstOption && availableOptionsMap.value[firstGroup]?.[firstOption as string]) {
        selectedOptions.value = {
          [firstGroup]: firstOption as string,
        }
      }
    }
    else {
      // Set options that correspond to available and in-stock variants
      Object.entries(validQueryOptions).forEach(([groupCode, optionCode]) => {
        if (typeof optionCode === 'string' && availableOptionsMap.value[groupCode]?.[optionCode]) {
          selectedOptions.value[groupCode] = optionCode
        }
      })
    }

    queryInitialized.value = true
  }

  function updateQueryParams() {
    if (!import.meta.client)
      return

    const query = {
      ...route.query,
      ...selectedOptions.value,
    }

    // Add or update variantId if we have a selected variant
    if (query.variantId && selectedVariant.value?.id) {
      query.variantId = selectedVariant.value.id.toString()
    }
    else {
      // Remove variantId if no variant is selected
      delete query.variantId
    }

    // Remove empty options from query
    Object.keys(query).forEach((key) => {
      if (!query[key])
        delete query[key]
    })

    void router.replace({ query })
  }

  watch(() => route.params.id, () => {
    selectedOptions.value = {}
  })

  const hasViewItemFired = ref(false)
  watch([product, queryInitialized], ([newProduct, queryInitialized]) => {
    if (import.meta.client && newProduct?.id && queryInitialized) {
      ecommerce.viewItem(newProduct, selectedVariant.value?.id)
      hasViewItemFired.value = true
    }
  }, { immediate: true })

  watch([selectedVariant, hasViewItemFired], ([newVariant, hasViewItemFired], [oldVariant]) => {
    if (import.meta.client && selectedVariant.value && hasViewItemFired && newVariant && newVariant.id !== oldVariant?.id) {
      ecommerce.viewItem(newVariant)
    }
  })

  const images = computed(() => {
    if (!product.value)
      return undefined

    const productFeaturedImage = product.value?.featuredAsset
      ? [{ ...product.value.featuredAsset, alt: product.value.name }]
      : []

    const productImages = product.value?.assets?.length
      ? product.value.assets.map(asset => ({ ...asset, alt: product.value?.name ?? '' }))
      : []

    const variantImages = productVariants.value?.length
      ? productVariants.value.map(variant => variant.featuredAsset)
          .filter(Boolean)
          .map((image, index) => ({
            ...image,
            alt: productVariants.value?.[index]?.name ?? product.value?.name ?? '',
          }))
      : []

    const selectedVariantFeaturedImage = selectedVariant.value?.featuredAsset
      ? [{
          ...selectedVariant.value.featuredAsset,
          alt: ((product.value?.name ?? '') + (selectedVariant.value.options ?? []).map(o => o.name).join(' ')).trim(),
        }]
      : []

    return [
      ...productFeaturedImage,
      ...productImages,
      ...variantImages,
      ...selectedVariantFeaturedImage,
    ].filter((image, index, self) => image?.id && image?.preview && self.findIndex(i => i.id === image.id) === index) as (SearchResultAssetFragment & { alt: string })[]
  })

  const variantImages = computed(() => {
    if (!product.value)
      return undefined

    const selectedVariantColor = selectedOptions.value?.color
    const filteredVariants = selectedVariantColor
      ? productVariants.value?.filter(variant => variant.options?.find(o => o.group.code === 'color')?.code === selectedVariantColor)
      : productVariants.value

    const variantImages = productVariants.value?.length
      ? filteredVariants?.flatMap(variant => ([...(variant.assets ?? []), variant.featuredAsset]
          .filter(Boolean)
          .map(asset => ({
            ...asset,
            alt: variant.name ?? product.value?.name ?? '',
          }))
        ))
      : []

    const selectedVariantFeaturedImage = selectedVariant.value?.featuredAsset
      ? [{
          ...selectedVariant.value.featuredAsset,
          alt: ((product.value?.name ?? '') + (selectedVariant.value.options ?? []).map(o => o.name).join(' ')).trim(),
        }]
      : []

    return [
      ...variantImages!,
      ...selectedVariantFeaturedImage,
    ].filter((image, index, self) => image?.id && image?.preview && self.findIndex(i => i.id === image.id) === index) as (SearchResultAssetFragment & { alt: string })[]
  })

  return {
    product,
    status,
    images,
    variantImages,
    productVariants,
    productCollectionId,
    price,
    priceBeforeDiscount,
    productOptionGroups,
    availableProductOptions,
    productBreadcrumbs,
    selectedVariant,
    activeVariant,
    amountSaved,
    discountPercentage,
    selectedOptions,
    hoveredOption,
    allOptionsSelected,
    availableOptionsMap,
    previewOptionsMap,
    initializeFromQuery,
    handleOptionChange,
    isColorGroupWithImages,
    handleProductOptionMouseOver,
    handleProductOptionMouseLeave,
    reset,
    fetchProduct,
  }
})
